'use client'

import { MouseEvent } from 'react'

import {
  faCheck,
  faEllipsisH,
  faTrashCan,
} from '@document-crunch/pro-regular-svg-icons'

import {
  Button,
  Icon,
  DropdownMenu,
  DropdownMenuItem,
  IconColor,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/dc-shared-web/src'

import { formatDate } from '@/app/core/components/notification/utils'

type NotificationMenuProps = {
  notificationId: string
  isUnread: boolean
  isHovered: boolean
  isMenuOpened: boolean
  setIsMenuOpened: (isOpen: boolean) => void
  date: Date
  onMarkAsRead: (e: MouseEvent<HTMLDivElement>) => void
  onDelete: (e: MouseEvent<HTMLDivElement>) => void
}

/**
 * A reusable menu component for notifications that shows either a dropdown menu
 * or date and unread indicator based on hover state
 */
export const NotificationMenu = ({
  isUnread,
  isHovered,
  isMenuOpened,
  setIsMenuOpened,
  date,
  onMarkAsRead,
  onDelete,
}: NotificationMenuProps) => {
  return (
    <div className='tw-flex tw-justify-end tw-basis-16 tw-shrink-0'>
      {isHovered || isMenuOpened ? (
        <DropdownMenu open={isMenuOpened} onOpenChange={setIsMenuOpened}>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='mini'
              className='tw-outline-none data-[state=open]:tw-bg-neutral-100 hover:tw-bg-neutral-100'
              data-testid='notification-action-button'
            >
              <Icon icon={faEllipsisH} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='tw-rounded-lg tw-bg-white tw-shadow-[0_2px_4px_#00000014,0_4px_12px_#0000001f] tw-p-1 tw-gap-0'
            sideOffset={4}
            align='end'
          >
            {isUnread && (
              <DropdownMenuItem
                className='tw-cursor-pointer tw-flex tw-items-center tw-gap-2'
                onClick={onMarkAsRead}
                data-testid='notification-item-mark-as-read-button'
              >
                <Icon icon={faCheck} color={IconColor.secondary} />
                <span className='tw-text-sm tw-text-neutral-700'>
                  Mark as read
                </span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className='tw-cursor-pointer tw-flex tw-items-center tw-gap-2'
              onClick={onDelete}
              data-testid='notification-item-delete-button'
            >
              <Icon icon={faTrashCan} color={IconColor.secondary} />
              <span className='tw-text-sm tw-text-neutral-700'>
                Delete notification
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className='tw-flex tw-gap-2 tw-whitespace-nowrap tw-h-fit tw-items-center'>
          <span className='tw-text-sm tw-text-neutral-400'>
            {formatDate(date)}
          </span>
          {isUnread && (
            <span
              className='tw-w-2.5 tw-h-2.5 tw-bg-red-500 tw-rounded-full'
              data-testid='notification-item-unread-icon'
            />
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationMenu
